import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";
import { Client, Country, CountryService, Population, PopulationService } from "../../../../../swaggerServices";
import { ClientRouteResolver } from "../client-route-resolver.service";
import { UserInfoService } from "../../../user-info.service";
import { ConfigStateService } from '../../../config/config-state.service';
import { Tenant } from '../../../../shared/types/tenant.enum';
import { TenantConfigService } from '../../../../shared/services/tenant-config.service';
import { TranslocoService} from "@jsverse/transloco"; 
import { RubixDocumentsService } from "../../../tax-returns/tax-return/rubix-documents/rubix-documents.service"
import { DropdownListOption } from '../../../../shared/components/search-dropdown/search-dropdown.component';
import { byId } from '../../../../shared/util/byId';

declare var $: any;

// swagger Client interface doesn't contain years obj
interface IClient extends Client {
  populations: Population[];
}

@Component({
    selector: 'app-populations',
    templateUrl: './populations.component.html',
    styleUrls: ['./populations.component.scss'],
    standalone: false
})
export class PopulationsComponent implements OnInit, OnDestroy {
  client: IClient;
  populations: Population[];
  populationSearch: string;
  newPopulationName: string; // new population input  
  allCountries: Country[] = [];
  allCountriesById = {};
  userAccessCountriesIds: number[] = [];
  newPopulationCountryId: number = null;
  countryOptions: DropdownListOption[] = []
  defaultCountryId: number = null;
  showCountries: boolean = false;    
  population: Population = {}; // population to edit
  taxYr: number;
  subscriptions: Subscription[] = [];
  taxpayerAlias: string;
  uiConfig: TenantConfigService;
  hasCountryAccess: boolean;
  @ViewChild("countryDropDown") countryDropDown: ElementRef;
  @ViewChild('newPopulationModal') newPopulationModal: ElementRef;
  @ViewChild('newPopulationSuccessModal') newPopulationSuccessModal: ElementRef;

  constructor(private clientRouteResolver: ClientRouteResolver,
              private populationService: PopulationService,
              public userInfo: UserInfoService,
              private activatedRoute: ActivatedRoute,
              private configState: ConfigStateService,
              private translocoService: TranslocoService,
              private rubixDocumentService: RubixDocumentsService,
              private countryService: CountryService) {
  }

  ngOnInit() {
    this.subscriptions.push(this.clientRouteResolver.client().subscribe((client: any) => {
      this.client = client;
      this.populations = client.populations;
      this.rubixDocumentService.setClientForClientFileQuestionUploads(this.client);
    }));

    this.subscriptions.push(this.activatedRoute.queryParams.subscribe(params => {
      if (params.taxYear) this.taxYr = params.taxYear
    }));
    this.subscriptions.push(
      this.configState.loadConfig().subscribe(configState => {
        this.uiConfig = new TenantConfigService(configState);
        this.taxpayerAlias = this.uiConfig.config.taxpayerAlias;
        this.translocoService.selectTranslate('tax-payer-alias').subscribe(value => this.taxpayerAlias = value);
      })
    );
        
    this.subscriptions.push(this.countryService.getCountrys().subscribe((c) => {
      this.allCountries = c;
      this.allCountriesById = byId(c);
      this.showCountries = c?.length > 1;      
    }));
    
    if (this.userInfo.countries.length === 1) this.defaultCountryId = this.userInfo.countries[0].id;    

    this.userInfo.countries.forEach((c) => {
      this.userAccessCountriesIds.push(c.id);
      this.countryOptions.push({ id: c.id, name: c.countryCode, value: `${c.id}`, selected: c.id === this.defaultCountryId })
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  // shows new population form in modal
  openPopulationModal() {
    $(this.newPopulationModal.nativeElement)
      .modal({
        onShow: () => {
          this.newPopulationName = "";
          // If there is only one country in the list it will be preselected as the default and make the dropdown hidden.          
          this.newPopulationCountryId = this.defaultCountryId;          
        }
      }).modal('show');
    
    $(this.countryDropDown.nativeElement).dropdown({
      values: this.countryOptions,
      placeholder: "Select...",
      fullTextSearch: "exact",
      preserveHTML: false,
      onChange: (value: string) => {
        if (value) {
          this.newPopulationCountryId = parseInt(value);          
        }
      },
    });        
  }

  // saves a population then show success modal and refresh populations list
  // TODO: errors to be handled by global component
  saveNewPopulation() {
    if (!this.newPopulationName) return false;

    this.populationService.addPopulation({
      name: this.newPopulationName,
      clientId: this.client.id,
      year: this.taxYr,
      countryId: this.configState.featureFlags.onlyAllowSingleCountryPerClient? this.client.countryId : this.newPopulationCountryId
    }).subscribe(
      () => {
        $(this.newPopulationSuccessModal.nativeElement).modal('show');
        this.getPopulations();
      });
  }

  getPopulations() {
    this.clientRouteResolver.resolveForClient(this.client.id, String(this.taxYr)).subscribe();
  }

  canEditClientRules() {
    if (this.configState.featureFlags.clientRulesEditableByUsers) {
      return true;
    }
    return this.userInfo.isClientAdmin;
  }
  
  getPopulationCountryCode(countryId: number) {    
    return this.allCountriesById[countryId]?.countryCode || "";
  }

}
